import React, { useState, useEffect, useRef, useMemo, useId } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { EnvironmentDetails, Definition, DataBuild, UpdateEnvironmentRequest, HistoricalChange } from '../types';
import networkManager from '../services/NetworkManager';
import { useModal } from '../contexts/ModalContext';
import { ModalActions, SearchableDropdown } from '../components/ModalComponents';
import { useNotification } from '../contexts/NotificationContext';

const EnvironmentDetailsPage: React.FC = () => {
  const { envName } = useParams<{ envName: string }>();
  const navigate = useNavigate();
  const { openModal, closeModal } = useModal();
  const { showNotification } = useNotification();
  const passwordFieldId = useId();
  
  const [environment, setEnvironment] = useState<EnvironmentDetails | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [definitions, setDefinitions] = useState<Definition[]>([]);
  const [dataBuilds, setDataBuilds] = useState<DataBuild[]>([]);
  const [loadingDefinitions, setLoadingDefinitions] = useState(false);
  const [loadingDataBuilds, setLoadingDataBuilds] = useState(false);
  
  // Edit modal states
  const [updateData, setUpdateData] = useState<UpdateEnvironmentRequest>({});
  const [definitionSearchTerm, setDefinitionSearchTerm] = useState('');
  const [selectedDefinition, setSelectedDefinition] = useState<Definition | null>(null);
  const [gamedataSearchTerm, setGamedataSearchTerm] = useState('');
  const [selectedGamedata, setSelectedGamedata] = useState<DataBuild | null>(null);
  const [copyFeedback, setCopyFeedback] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  
  // Password settings states
  const [requirePassword, setRequirePassword] = useState(false);
  const [password, setPassword] = useState('');
  const [savingPassword, setSavingPassword] = useState(false);

  useEffect(() => {
    if (envName) {
      fetchEnvironmentDetails();
    }
  }, [envName]);


  const fetchEnvironmentDetails = async () => {
    try {
      setLoading(true);
      const data = await networkManager.getEnvironmentDetails(envName!);
      setEnvironment(data);
      setError(null);
      
      // Initialize password settings from environment data
      // Check if deploymentPassword exists and is not empty
      const hasPassword = data.environmentConfigs?.deploymentPassword && 
                         data.environmentConfigs.deploymentPassword.trim() !== '';
      setRequirePassword(hasPassword || false);
      // Don't set the password value for security reasons - it should be empty
      setPassword('');
      
      // Load definitions and data builds for name resolution
      await Promise.all([
        loadDefinitions(),
        loadDataBuilds()
      ]);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch environment details');
    } finally {
      setLoading(false);
    }
  };

  const loadDefinitions = async () => {
    try {
      setLoadingDefinitions(true);
      const data = await networkManager.getDefinitions();
      setDefinitions(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Error loading definitions:', err);
    } finally {
      setLoadingDefinitions(false);
    }
  };

  const loadDataBuilds = async () => {
    try {
      setLoadingDataBuilds(true);
      const data = await networkManager.getDataBuilds();
      // Filter only successfully built gamedata
      const successfulDataBuilds = Array.isArray(data) 
        ? data.filter(build => build.status === 'success')
        : [];
      setDataBuilds(successfulDataBuilds);
    } catch (err) {
      console.error('Error loading data builds:', err);
    } finally {
      setLoadingDataBuilds(false);
    }
  };

  const getDefinitionName = (definitionId: string): string => {
    const definition = definitions.find(def => def._id === definitionId);
    return definition ? definition.title : definitionId;
  };

  const getDataBuildName = (dataBuildId: string): string => {
    const dataBuild = dataBuilds.find(build => build._id === dataBuildId);
    return dataBuild ? dataBuild.title : dataBuildId;
  };

  const isGamedataEmpty = (gamedata: any): boolean => {
    if (!gamedata) return true;
    
    // Check if all relevant fields are empty or have default values
    return (
      !gamedata.id || 
      gamedata.id === '' ||
      !gamedata.title || 
      gamedata.title === '' ||
      !gamedata.author || 
      gamedata.author === '' ||
      !gamedata.description || 
      gamedata.description === '' ||
      !gamedata.definitionId || 
      gamedata.definitionId === '' ||
      !gamedata.gamedataURL || 
      gamedata.gamedataURL === '' ||
      (gamedata.createdAt && gamedata.createdAt === "0001-01-01T00:00:00Z")
    );
  };

  const isDefinitionEmpty = (definition: any): boolean => {
    if (!definition) return true;
    
    // Check if all relevant fields are empty or have default values
    // Note: id is optional, so we don't check for it
    return (
      !definition.title || 
      definition.title === '' ||
      !definition.author || 
      definition.author === '' ||
      !definition.description || 
      definition.description === '' ||
      (definition.createdAt && definition.createdAt === "0001-01-01T00:00:00Z")
    );
  };

  const isPublisherEmpty = (publisher: any): boolean => {
    if (!publisher) return true;
    
    return (
      !publisher.id || 
      publisher.id === '' ||
      !publisher.name || 
      publisher.name === ''
    );
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('es-ES', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const formatActionName = (action: string): string => {
    const actionMap: Record<string, string> = {
      'create': 'Created',
      'update_definition': 'Updated Definition',
      'update_data': 'Updated Gamedata',
      'update_password': 'Updated Password',
    };
    return actionMap[action] || action;
  };

  const formatChangeValue = (value: string, action: string): string => {
    if (!value || value === '') return 'N/A';
    
    // If it's an ID (looks like MongoDB ObjectId), try to resolve it
    if (/^[0-9a-fA-F]{24}$/.test(value)) {
      if (action === 'update_definition') {
        return getDefinitionName(value);
      } else if (action === 'update_data') {
        return getDataBuildName(value);
      }
    }
    
    return value;
  };

  const getActionIcon = (action: string) => {
    const iconColor = getActionIconColor(action);
    switch (action) {
      case 'create':
        return (
          <svg className={`w-4 h-4 ${iconColor}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
          </svg>
        );
      case 'update_definition':
        return (
          <svg className={`w-4 h-4 ${iconColor}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
          </svg>
        );
      case 'update_data':
        return (
          <svg className={`w-4 h-4 ${iconColor}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
          </svg>
        );
      case 'update_password':
        return (
          <svg className={`w-4 h-4 ${iconColor}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
          </svg>
        );
      default:
        return (
          <svg className={`w-4 h-4 ${iconColor}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
          </svg>
        );
    }
  };

  const getActionBadgeColor = (action: string): string => {
    switch (action) {
      case 'create':
        return 'bg-emerald-500/10 text-emerald-600 border-emerald-500/20';
      case 'update_definition':
        return 'bg-violet-500/10 text-violet-600 border-violet-500/20';
      case 'update_data':
        return 'bg-amber-500/10 text-amber-600 border-amber-500/20';
      case 'update_password':
        return 'bg-blue-500/10 text-blue-600 border-blue-500/20';
      default:
        return 'bg-slate-500/10 text-slate-600 border-slate-500/20';
    }
  };

  const getActionIconColor = (action: string): string => {
    switch (action) {
      case 'create':
        return 'text-emerald-500';
      case 'update_definition':
        return 'text-violet-500';
      case 'update_data':
        return 'text-amber-500';
      case 'update_password':
        return 'text-blue-500';
      default:
        return 'text-slate-500';
    }
  };

  const getBackendApiUrl = () => {
    const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:3001/api';
    const orgId = networkManager.getOrganizationId();
    if (!orgId) return 'Organization ID not found';
    if (!environment) return 'Environment not loaded';
    return `${backendUrl}/public/${orgId}/environment/${environment.environmentName}`;
  };

  const copyToClipboard = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopyFeedback(true);
      setTimeout(() => setCopyFeedback(false), 2000); // Hide feedback after 2 seconds
    } catch (err) {
      console.error('Failed to copy to clipboard:', err);
    }
  };

  const handleBack = () => {
    navigate('/environments');
  };

  const filteredDefinitions = useMemo(() => {
    return definitionSearchTerm.trim() === '' 
      ? (definitions || []).slice(0, 5)
      : (definitions || []).filter(def =>
          def.title.toLowerCase().includes(definitionSearchTerm.toLowerCase())
        ).slice(0, 5);
  }, [definitions, definitionSearchTerm]);

  const filteredGamedata = useMemo(() => {
    return gamedataSearchTerm.trim() === '' 
      ? (dataBuilds || []).slice(0, 5)
      : (dataBuilds || []).filter(build =>
          build.title.toLowerCase().includes(gamedataSearchTerm.toLowerCase())
        ).slice(0, 5);
  }, [dataBuilds, gamedataSearchTerm]);

  const getEditModalContent = () => {
    return (
      <form onSubmit={handleUpdateEnvironment} className="space-y-6">
        <SearchableDropdown
          label="Definition"
          value={definitionSearchTerm}
          onChange={handleDefinitionSearch}
          onFocus={() => {}}
          placeholder={loadingDefinitions ? "Loading definitions..." : "Search for a definition"}
          disabled={loadingDefinitions}
          loading={loadingDefinitions}
          items={filteredDefinitions}
          onSelect={handleDefinitionSelect}
          selectedItem={selectedDefinition}
          onRemove={() => {
            setSelectedDefinition(null);
            setDefinitionSearchTerm('');
            setUpdateData(prev => ({ ...prev, DefinitionID: '' }));
          }}
        />

        <SearchableDropdown
          label="Gamedata"
          value={gamedataSearchTerm}
          onChange={handleGamedataSearch}
          onFocus={() => {}}
          placeholder={loadingDataBuilds ? "Loading gamedata..." : "Search for gamedata"}
          disabled={loadingDataBuilds}
          loading={loadingDataBuilds}
          items={filteredGamedata}
          onSelect={handleGamedataSelect}
          selectedItem={selectedGamedata}
          onRemove={() => {
            setSelectedGamedata(null);
            setGamedataSearchTerm('');
            setUpdateData(prev => ({ ...prev, GamedataID: '' }));
          }}
        />

        <ModalActions
          actions={[
            {
              label: 'Cancel',
              onClick: () => {
                closeModal('edit-environment');
                setIsEditModalOpen(false);
                setSelectedDefinition(null);
                setSelectedGamedata(null);
                setDefinitionSearchTerm('');
                setGamedataSearchTerm('');
                setUpdateData({});
              },
              variant: 'secondary'
            },
            {
              label: 'Update Environment',
              type: 'submit',
              variant: 'primary',
              disabled: !updateData.DefinitionID && !updateData.GamedataID
            }
          ]}
        />
      </form>
    );
  };

  const openEditModal = async () => {
    if (!environment) return;
    
    // Load definitions and data builds for search
    await Promise.all([loadDefinitions(), loadDataBuilds()]);
    
    // Set initial values if they exist
    // Check for Definition in the environment details
    if (environment.Definition && !isDefinitionEmpty(environment.Definition)) {
      // Try to find definition by id first, then by title as fallback
      let definition: Definition | undefined = undefined;
      if (environment.Definition.id) {
        definition = definitions.find(def => def._id === environment.Definition!.id);
      }
      if (!definition && environment.Definition.title) {
        definition = definitions.find(def => def.title === environment.Definition!.title);
      }
      
      if (definition) {
        setSelectedDefinition(definition);
        setDefinitionSearchTerm(definition.title);
        setUpdateData(prev => ({ ...prev, DefinitionID: definition!._id }));
      }
    }
    
    // Check for Gamedata in the environment details
    if (environment.Gamedata?.id && !isGamedataEmpty(environment.Gamedata)) {
      const gamedata = dataBuilds.find(build => build._id === environment.Gamedata?.id);
      if (gamedata) {
        setSelectedGamedata(gamedata);
        setGamedataSearchTerm(gamedata.title);
        setUpdateData(prev => ({ ...prev, GamedataID: gamedata._id }));
      }
    }
    
    setIsEditModalOpen(true);
    openModal({
      id: 'edit-environment',
      title: `Edit Environment: ${environment.environmentName}`,
      content: getEditModalContent(),
      size: 'lg'
    });
  };

  // Update modal content when states change
  useEffect(() => {
    if (isEditModalOpen && environment) {
      openModal({
        id: 'edit-environment',
        title: `Edit Environment: ${environment.environmentName}`,
        content: getEditModalContent(),
        size: 'lg'
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEditModalOpen, selectedDefinition, selectedGamedata, definitionSearchTerm, gamedataSearchTerm, updateData, loadingDefinitions, loadingDataBuilds, definitions, dataBuilds]);

  const handleUpdateEnvironment = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!environment || !envName) return;
    
    try {
      const environmentId = envName;
      await networkManager.updateEnvironment(environmentId, updateData);
      closeModal('edit-environment');
      setIsEditModalOpen(false);
      setSelectedDefinition(null);
      setSelectedGamedata(null);
      setDefinitionSearchTerm('');
      setGamedataSearchTerm('');
      setUpdateData({});
      
      // Refresh the environment details
      await fetchEnvironmentDetails();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update environment');
    }
  };

  const handleDefinitionSearch = (searchValue: string) => {
    setDefinitionSearchTerm(searchValue);
    
    if (searchValue.trim() === '') {
      setSelectedDefinition(null);
      setUpdateData(prev => ({ ...prev, DefinitionID: '' }));
      return;
    }

    // Find definition by title (case-insensitive)
    const found = definitions.find(def => 
      def.title.toLowerCase().includes(searchValue.toLowerCase())
    );
    
    if (found) {
      setSelectedDefinition(found);
      setUpdateData(prev => ({ ...prev, DefinitionID: found._id }));
    } else {
      setSelectedDefinition(null);
      setUpdateData(prev => ({ ...prev, DefinitionID: '' }));
    }
  };

  const handleDefinitionSelect = (definition: Definition) => {
    setSelectedDefinition(definition);
    setDefinitionSearchTerm(definition.title);
    setUpdateData(prev => ({ ...prev, DefinitionID: definition._id }));
  };

  const handleGamedataSearch = (searchValue: string) => {
    setGamedataSearchTerm(searchValue);
    
    if (searchValue.trim() === '') {
      setSelectedGamedata(null);
      setUpdateData(prev => ({ ...prev, GamedataID: '' }));
      return;
    }

    // Find gamedata by title (case-insensitive)
    const found = dataBuilds.find(build => 
      build.title.toLowerCase().includes(searchValue.toLowerCase())
    );
    
    if (found) {
      setSelectedGamedata(found);
      setUpdateData(prev => ({ ...prev, GamedataID: found._id }));
    } else {
      setSelectedGamedata(null);
      setUpdateData(prev => ({ ...prev, GamedataID: '' }));
    }
  };

  const handleGamedataSelect = (gamedata: DataBuild) => {
    setSelectedGamedata(gamedata);
    setGamedataSearchTerm(gamedata.title);
    setUpdateData(prev => ({ ...prev, GamedataID: gamedata._id }));
  };

  const handleSavePasswordSettings = async () => {
    if (!environment || !envName) return;
    
    try {
      setSavingPassword(true);
      await networkManager.updateEnvironmentConfigs(envName, {
        deploymentPassword: requirePassword && password ? password : ''
      });
      
      // Refresh the environment details
      await fetchEnvironmentDetails();
      
      showNotification(
        'Password settings saved successfully',
        'success',
        3000
      );
    } catch (err) {
      showNotification(
        err instanceof Error ? err.message : 'Failed to save password settings',
        'error',
        5000
      );
    } finally {
      setSavingPassword(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
          {error}
        </div>
        <button
          onClick={handleBack}
          className="bg-gray-600 hover:bg-gray-700 text-white font-medium py-2 px-4 rounded-lg transition-colors"
        >
          Back to Environments
        </button>
      </div>
    );
  }

  if (!environment) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Environment not found</h2>
          <button
            onClick={handleBack}
            className="bg-gray-600 hover:bg-gray-700 text-white font-medium py-2 px-4 rounded-lg transition-colors"
          >
            Back to Environments
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      {/* Header Section */}
      <div className="mb-8">
        <button
          onClick={handleBack}
          className="flex items-center text-blue-600 hover:text-blue-800 mb-6 transition-colors"
        >
          <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
          Back to Environments
        </button>
        
        <div className="bg-gradient-to-r from-blue-600 to-blue-800 rounded-lg p-6 text-white">
          <div className="flex justify-between items-start">
            <div>
              <h1 className="text-4xl font-bold mb-2">{environment.environmentName}</h1>
              {environment.environmentDescription && (
                <p className="text-blue-100 text-lg">{environment.environmentDescription}</p>
              )}
            </div>
            <button
              onClick={openEditModal}
              className="bg-white bg-opacity-20 hover:bg-opacity-30 text-white font-medium py-2 px-4 rounded-lg transition-colors flex items-center"
            >
              <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
              </svg>
              Edit Environment
            </button>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Environment Information */}
        <div className="bg-white shadow-lg rounded-xl p-6 border border-gray-100">
          <div className="flex items-center mb-6">
            <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center mr-3">
              <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
              </svg>
            </div>
            <h2 className="text-2xl font-bold text-gray-900">Environment Information</h2>
          </div>
          
          <div className="space-y-6">
            <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Created</label>
                <p className="text-sm text-gray-900 font-medium">{formatDate(environment.environmentCreatedAt)}</p>
              </div>
              <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center">
                <svg className="w-4 h-4 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
            </div>
            
            <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Last Updated</label>
                <p className="text-sm text-gray-900 font-medium">{formatDate(environment.environmentUpdatedAt)}</p>
              </div>
              <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                <svg className="w-4 h-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
              </div>
            </div>
            
            {environment.definitionPublisher && !isPublisherEmpty(environment.definitionPublisher) && (
              <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Definition Publisher</label>
                  <p className="text-sm text-gray-900 font-medium">{environment.definitionPublisher.name}</p>
                </div>
                <div className="w-8 h-8 bg-purple-100 rounded-full flex items-center justify-center">
                  <svg className="w-4 h-4 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                  </svg>
                </div>
              </div>
            )}
            
            {environment.dataPublisher && !isPublisherEmpty(environment.dataPublisher) && (
              <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Data Publisher</label>
                  <p className="text-sm text-gray-900 font-medium">{environment.dataPublisher.name}</p>
                </div>
                <div className="w-8 h-8 bg-orange-100 rounded-full flex items-center justify-center">
                  <svg className="w-4 h-4 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                  </svg>
                </div>
              </div>
            )}
            
            <div className="flex items-center justify-between p-4 bg-blue-50 rounded-lg border border-blue-100">
              <div className="flex-1 min-w-0">
                <div className="flex items-center space-x-2 mb-1">
                  <label className="text-sm font-medium text-blue-700">Backend API URL</label>
                  <button
                    onClick={() => copyToClipboard(getBackendApiUrl())}
                    className={`transition-all duration-200 ${
                      copyFeedback 
                        ? 'text-green-600 scale-110' 
                        : 'text-blue-600 hover:text-blue-800'
                    }`}
                    title={copyFeedback ? "Copied!" : "Copy API URL to clipboard"}
                  >
                    {copyFeedback ? (
                      <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                      </svg>
                    ) : (
                      <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"></path>
                      </svg>
                    )}
                  </button>
                  {copyFeedback && (
                    <span className="text-xs text-green-600 font-medium animate-pulse">
                      Copied!
                    </span>
                  )}
                </div>
                <a
                  href={getBackendApiUrl()}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-sm text-blue-600 hover:text-blue-800 break-all font-medium block"
                  title={getBackendApiUrl()}
                >
                  {getBackendApiUrl()}
                </a>
              </div>
              <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
                <svg className="w-4 h-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
                </svg>
              </div>
            </div>

            {/* Require Password for Deploy */}
            <div className="p-4 bg-gray-50 rounded-lg">
              <div className="flex items-center justify-between mb-3">
                <label htmlFor="requirePassword" className="text-sm font-medium text-gray-700">
                Require password for deployment
                </label>
                <button
                  type="button"
                  role="switch"
                  aria-checked={requirePassword}
                  onClick={async () => {
                    const newRequirePassword = !requirePassword;
                    setRequirePassword(newRequirePassword);
                    if (requirePassword) {
                      setPassword('');
                      // When disabling, save with empty deploymentPassword
                      if (!newRequirePassword && environment && envName) {
                        try {
                          setSavingPassword(true);
                          await networkManager.updateEnvironmentConfigs(envName, {
                            deploymentPassword: ''
                          });
                          await fetchEnvironmentDetails();
                          showNotification(
                            'Password settings saved successfully',
                            'success',
                            3000
                          );
                        } catch (err) {
                          showNotification(
                            err instanceof Error ? err.message : 'Failed to save password settings',
                            'error',
                            5000
                          );
                        } finally {
                          setSavingPassword(false);
                        }
                      }
                    }
                  }}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${
                    requirePassword ? 'bg-blue-600' : 'bg-gray-300'
                  }`}
                >
                  <span
                    className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                      requirePassword ? 'translate-x-6' : 'translate-x-1'
                    }`}
                  />
                </button>
              </div>
              
              <div 
                className={`overflow-hidden transition-all duration-300 ease-in-out ${
                  requirePassword ? 'max-h-20 opacity-100' : 'max-h-0 opacity-0'
                }`}
              >
                {requirePassword && (
                  <div className="flex items-center gap-2 pt-2">
                    <div className="flex-1">
                      <input
                        type="password"
                        id={passwordFieldId}
                        name={passwordFieldId}
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        placeholder="Enter password"
                        autoComplete="new-password"
                        data-lpignore="true"
                        data-form-type="other"
                        data-1p-ignore="true"
                        spellCheck="false"
                        className="flex-1 w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-1 focus:ring-blue-500 focus:border-blue-500 bg-white"
                      />
                    </div>
                    <button
                      onClick={handleSavePasswordSettings}
                      disabled={savingPassword || !password.trim()}
                      className={`px-4 py-2 text-sm font-medium rounded-md transition-colors whitespace-nowrap ${
                        savingPassword || !password.trim()
                          ? 'bg-gray-200 text-gray-500 cursor-not-allowed'
                          : 'bg-blue-600 hover:bg-blue-700 text-white'
                      }`}
                    >
                      {savingPassword ? 'Saving...' : 'Save'}
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Definition Information */}
        {environment && environment.Definition && !isDefinitionEmpty(environment.Definition) ? (
          <div className="bg-white shadow-lg rounded-xl p-6 border border-gray-100">
            <div className="flex items-center mb-6">
              <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center mr-3">
                <svg className="w-6 h-6 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
              </div>
              <h2 className="text-2xl font-bold text-gray-900">Definition</h2>
            </div>
            
            <div className="space-y-6">
              <div className="p-4 bg-purple-50 rounded-lg border border-purple-100">
                <label className="block text-sm font-medium text-purple-700 mb-2">Title</label>
                <p className="text-lg font-semibold text-gray-900">{environment.Definition.title}</p>
              </div>
              
              <div className="p-4 bg-gray-50 rounded-lg">
                <label className="block text-sm font-medium text-gray-700 mb-2">Author</label>
                <p className="text-sm text-gray-900 font-medium">{environment.Definition.author}</p>
              </div>
              
              <div className="p-4 bg-gray-50 rounded-lg">
                <label className="block text-sm font-medium text-gray-700 mb-2">Description</label>
                <p className="text-sm text-gray-900">{environment.Definition.description}</p>
              </div>
              
              <div className="p-4 bg-gray-50 rounded-lg">
                <label className="block text-sm font-medium text-gray-700 mb-2">Created</label>
                <p className="text-sm text-gray-900 font-medium">{formatDate(environment.Definition.createdAt)}</p>
              </div>
              
              {environment.Gamedata?.definitionId && (
                <Link
                  to={`/definitions/${environment.Gamedata.definitionId}`}
                  className="block w-full bg-purple-600 hover:bg-purple-700 text-white font-medium py-3 px-4 rounded-lg text-center transition-colors"
                >
                  View Definition Details
                </Link>
              )}
            </div>
          </div>
        ) : (
          <div className="bg-white shadow-lg rounded-xl p-6 border border-gray-100">
            <div className="flex items-center mb-6">
              <div className="w-10 h-10 bg-gray-100 rounded-lg flex items-center justify-center mr-3">
                <svg className="w-6 h-6 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
              </div>
              <h2 className="text-2xl font-bold text-gray-900">Definition</h2>
            </div>
            
            <div className="text-center py-8">
              <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
              </div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">No Definition Associated</h3>
              <p className="text-gray-500 mb-4">
                This environment doesn't have a definition associated yet. You can add one by editing the environment.
              </p>
              <button
                onClick={openEditModal}
                className="inline-flex items-center px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white font-medium rounded-lg transition-colors"
              >
                <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                </svg>
                Edit Environment
              </button>
            </div>
          </div>
        )}

        {/* Gamedata Information */}
        {environment && environment.Gamedata && !isGamedataEmpty(environment.Gamedata) ? (
          <div className="bg-white shadow-lg rounded-xl p-6 border border-gray-100 lg:col-span-2">
            <div className="flex items-center mb-6">
              <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center mr-3">
                <svg className="w-6 h-6 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                </svg>
              </div>
              <h2 className="text-2xl font-bold text-gray-900">Gamedata</h2>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div className="space-y-6">
                <div className="p-4 bg-orange-50 rounded-lg border border-orange-100">
                  <label className="block text-sm font-medium text-orange-700 mb-2">Title</label>
                  <p className="text-lg font-semibold text-gray-900">{environment.Gamedata.title}</p>
                </div>
                
                <div className="p-4 bg-gray-50 rounded-lg">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Author</label>
                  <div className="flex items-center space-x-2">
                    <svg className="h-4 w-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                    </svg>
                    <p className="text-sm text-gray-900 font-medium">{environment.Gamedata.author}</p>
                  </div>
                </div>
                
                <div className="p-4 bg-gray-50 rounded-lg">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Description</label>
                  <p className="text-sm text-gray-900">{environment.Gamedata.description}</p>
                </div>
                
                <div className="p-4 bg-gray-50 rounded-lg">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Definition</label>
                  <p className="text-sm text-gray-900 font-medium">
                    {loadingDefinitions ? (
                      <span className="text-gray-500">Loading...</span>
                    ) : (
                      getDefinitionName(environment.Gamedata.definitionId)
                    )}
                  </p>
                </div>
                
                <div className="p-4 bg-gray-50 rounded-lg">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Created</label>
                  <p className="text-sm text-gray-900 font-medium">{formatDate(environment.Gamedata.createdAt)}</p>
                </div>
              </div>
              
              <div className="space-y-6">
                <div className="p-4 bg-blue-50 rounded-lg border border-blue-100">
                  <label className="block text-sm font-medium text-blue-700 mb-2">Gamedata URL</label>
                  <a 
                    href={environment.Gamedata.gamedataURL} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="text-sm text-blue-600 hover:text-blue-800 break-all font-medium"
                  >
                    {environment.Gamedata.gamedataURL}
                  </a>
                </div>
                
                {environment.Gamedata.bundles && Object.keys(environment.Gamedata.bundles).length > 0 && (
                  <div className="p-4 bg-gray-50 rounded-lg">
                    <label className="block text-sm font-medium text-gray-700 mb-3">Available Bundles</label>
                    <div className="space-y-2">
                      {Object.keys(environment.Gamedata.bundles).map((bundleName) => (
                        <div key={bundleName} className="flex items-center justify-between p-3 bg-white rounded border">
                          <span className="text-sm font-medium text-gray-900">{bundleName}</span>
                          <div className="w-6 h-6 bg-green-100 rounded-full flex items-center justify-center">
                            <svg className="w-3 h-3 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                            </svg>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                
                <Link
                  to={`/data-builds/${environment.Gamedata.id}`}
                  className="block w-full bg-orange-600 hover:bg-orange-700 text-white font-medium py-3 px-4 rounded-lg text-center transition-colors"
                >
                  View Gamedata Details
                </Link>
              </div>
            </div>
          </div>
        ) : (
          <div className="bg-white shadow-lg rounded-xl p-6 border border-gray-100 lg:col-span-2">
            <div className="flex items-center mb-6">
              <div className="w-10 h-10 bg-gray-100 rounded-lg flex items-center justify-center mr-3">
                <svg className="w-6 h-6 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                </svg>
              </div>
              <h2 className="text-2xl font-bold text-gray-900">Gamedata</h2>
            </div>
            
            <div className="text-center py-8">
              <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                </svg>
              </div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">No Gamedata Associated</h3>
              <p className="text-gray-500 mb-4">
                This environment doesn't have gamedata associated yet. You can add one by editing the environment.
              </p>
              <button
                onClick={openEditModal}
                className="inline-flex items-center px-4 py-2 bg-orange-600 hover:bg-orange-700 text-white font-medium rounded-lg transition-colors"
              >
                <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                </svg>
                Edit Environment
              </button>
            </div>
          </div>
        )}

        {/* Historical Changes Section */}
        {environment.historicalChanges && environment.historicalChanges.length > 0 && (() => {
          // Sort by timestamp (newest first)
          const sortedChanges = [...environment.historicalChanges].sort((a, b) => 
            new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime()
          );
          
          return (
            <div className="bg-gradient-to-br from-slate-50 to-slate-100/50 shadow-lg rounded-xl p-6 border border-slate-200/60 lg:col-span-2">
              <div className="flex items-center mb-6">
                <div className="w-10 h-10 bg-slate-800 rounded-lg flex items-center justify-center mr-3 shadow-sm">
                  <svg className="w-6 h-6 text-slate-100" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <h2 className="text-2xl font-bold text-slate-900">Change History</h2>
              </div>
              
              <div className="space-y-2">
                {sortedChanges.map((change, index) => (
                  <div
                    key={index}
                    className="group relative bg-slate-800/5 backdrop-blur-sm border border-slate-300/30 rounded-lg p-4 transition-all hover:bg-slate-800/10 hover:border-slate-400/40 hover:shadow-sm"
                  >
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex items-start gap-3 flex-1 min-w-0">
                        <div className="flex-shrink-0 mt-0.5">
                          <div className={`w-8 h-8 rounded-md border flex items-center justify-center ${getActionBadgeColor(change.action)}`}>
                            {getActionIcon(change.action)}
                          </div>
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1.5">
                            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-md text-xs font-medium border ${getActionBadgeColor(change.action)}`}>
                              {formatActionName(change.action)}
                            </span>
                            <span className="text-xs text-slate-500">by</span>
                            <span className="text-xs font-semibold text-slate-700">{change.authorName}</span>
                          </div>
                          
                          {(change.previousValue || change.newValue) && (
                            <div className="flex flex-wrap gap-2 mt-2">
                              {change.previousValue && (
                                <div className="flex items-center gap-1.5 text-xs">
                                  <span className="text-slate-500 font-medium">From:</span>
                                  <span className="px-2 py-1 bg-slate-100/80 text-slate-700 rounded border border-slate-200/60 font-mono text-xs">
                                    {formatChangeValue(change.previousValue, change.action)}
                                  </span>
                                </div>
                              )}
                              {change.newValue && (
                                <div className="flex items-center gap-1.5 text-xs">
                                  <span className="text-slate-500 font-medium">To:</span>
                                  <span className="px-2 py-1 bg-slate-100/80 text-slate-700 rounded border border-slate-200/60 font-mono text-xs">
                                    {formatChangeValue(change.newValue, change.action)}
                                  </span>
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                      <div className="flex-shrink-0 text-xs text-slate-500 font-medium whitespace-nowrap">
                        {formatDate(change.timestamp)}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          );
        })()}
      </div>
    </div>
  );
};

export default EnvironmentDetailsPage;
